/* eslint-disable react-refresh/only-export-components -- component co-located with its scoping helper and type, split out of ProposalSelectionContext.tsx verbatim */
import { useCrmObject } from '@/lib/crm/store';
import type { EntityTypeName } from '@/types/crm';

import type { ProposedAction } from '../core/types';
import {
  isAssociationAddedPayload,
  isAssociationRemovedPayload,
} from '../core/types';

export interface AssociationActionSource {
  entityId: string;
  entityTypeName: EntityTypeName;
}

/**
 * Distinct (entityId, entityTypeName) pairs for the SOURCE entity of every
 * association action in `actions`, deduped by entity id. Scoped strictly to
 * these — never every projection entity — because a high-degree entity can
 * carry thousands of associations (FRONTEND_PATTERNS.md § Current Value
 * Pattern).
 */
export function getAssociationActionSources(
  actions: ProposedAction[]
): AssociationActionSource[] {
  const sources = new Map<string, AssociationActionSource>();
  for (const action of actions) {
    if (
      !isAssociationAddedPayload(action.payload) &&
      !isAssociationRemovedPayload(action.payload)
    ) {
      continue;
    }
    const { entity_id: entityId, entity_type_name: entityTypeName } =
      action.payload;
    // Timeline identifiers (`*_timeline`) are not CRM entity types — feeding
    // one to `useCrmObject` fails entity-type validation, so the fetch never
    // resolves and live association detection stays unknown. Skip them rather
    // than coercing the string into `EntityTypeName`.
    if (entityTypeName.endsWith('_timeline')) {
      continue;
    }
    if (!sources.has(entityId)) {
      sources.set(entityId, {
        entityId,
        entityTypeName: entityTypeName as EntityTypeName,
      });
    }
  }
  return [...sources.values()];
}

/**
 * Fetches a single association-action source entity WITH its associations,
 * so `getAssociationTargets` can read live association state for it.
 *
 * `useCrmObject` fetches one (org, type, id) triple per call, so a
 * dynamic-length list of sources must fan out into one component per id
 * rather than looping the hook inside the provider (Rules of Hooks forbid a
 * variable number of hook calls). This component renders nothing and reports
 * nothing back directly — the fetch writes into the shared CRM store, and
 * `ProposalSelectionProvider` reads the result from the same store
 * subscription it already holds for projection validity.
 */
export function AssociationSourceFetcher({
  orgId,
  entityId,
  entityTypeName,
}: {
  orgId: number;
  entityId: string;
  entityTypeName: EntityTypeName;
}) {
  useCrmObject(orgId, entityTypeName, entityId, { includeAssociations: true });
  return null;
}
